<script lang="ts">
	import { format } from 'svelte-i18n';
	// Design System Components
	import {
		Text,
		SidepanelLayout,
		SidepanelTabs,
		SidepanelContent,
		Modal,
		Button,
		Container,
		Icon,
		toast,
		ParameterCard,
	} from '@Shelly/shelly-design-system';
	// Stores
	import { scenes } from 'src/stores/lists/scenes';
	import { rooms } from 'src/stores/lists/rooms';
	import { sidePanelState, sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import { edit } from 'src/stores/ui/edit_lists';
	import type { ISceneObj } from 'src/types/SceneType';
	// Helpers
	import { handleSidePanelTabClick, scenesTabsConfig } from '../../sidepanel-tabs-helpers';
	import { _deepClone, capFirstChar } from 'src/helpers/utilities';
	import { canManageHomeEntities } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import {
		check_for_broken_conditions_or_actions,
		validate_scene,
	} from 'src/components/Scenes/helpers/validators';
	import { getDuplicateInfo, generateCopyName } from 'src/components/Scenes/helpers/sceneUtils';
	import { addRemoveDeviceIdsFromMetaInSceneObject } from 'src/components/Scenes/SceneWizard/Process/GenericComponents/helpers';
	import { postReq } from 'src/services/http';
	import { setSidePanelState } from '../../sidepanel-helpers';
	// Scene-specific tab components
	import SceneMain from './Tabs/SceneMain.svelte';
	import ScenePlay from './Tabs/ScenePlay.svelte';
	import SceneInfo from './Tabs/SceneInfo.svelte';
	import SceneScript from './Tabs/SceneScript.svelte';
	// Scene Wizard & Steps
	import SceneWizard from 'src/components/Scenes/SceneWizard/SceneWizard.svelte';
	import WhenStep from 'src/components/Scenes/SceneWizard/Steps/WhenStep.svelte';
	import DoStep from 'src/components/Scenes/SceneWizard/Steps/DoStep.svelte';
	import ActiveTimeStep from 'src/components/Scenes/SceneWizard/Steps/ActiveTimeStep.svelte';
	// Script scene (experimental)
	import { ScriptSceneEditor, type ScriptScene } from '@Shelly/scenes-script';
	import ShellyScriptEditor from 'src/components/DevicesSettings/Scripts/Editor/ShellyScriptEditor';
	import { _calculate_duration } from 'src/helpers/utilities';
	import { calculateWhenEnd } from 'src/helpers/interface_helpers';
	import { user_data } from 'src/stores/account/user';
	import AddEditInterval from 'src/components/Scenes/SceneWizard/Process/ActiveTime/AddEditInterval.svelte';
	import { isExperimentalEnabled } from 'src/helpers/user_settings_helpers';

	let tabIndex: number = 0;
	let tabs = scenesTabsConfig;
	let SCENE_SIDE_PANEL_CONTENT_CONFIG = [SceneMain, ScenePlay, SceneInfo];

	$: if (isExperimentalEnabled('inf2')) {
		if (scene?.infinno) {
			SCENE_SIDE_PANEL_CONTENT_CONFIG = [SceneMain];
			tabs = [scenesTabsConfig[0]];
		} else {
			SCENE_SIDE_PANEL_CONTENT_CONFIG.push(SceneScript as any);
			if (!tabs?.find((tab) => tab.id === 'script')) {
				tabs.push({
					icon: 'fat fa-code-simple',
					isActive: false,
					disabled: false,
					id: 'script',
				});
			}
		}
	} else {
		if (tabs?.find((tab) => tab.id === 'script')) {
			tabs = tabs.filter((tab) => tab.id !== 'script');
		}
	}

	/**
	 * Note: the whole preview is reactive, because of socket scenes update
	 * keep the scene sync on scenes store update
	 */

	$: id = $sidePanelStateCombined?.id;
	$: scene = $scenes[id] ? (_deepClone($scenes[id]) as ISceneObj) : undefined;

	$: name = scene?._meta?.name || '';
	$: roomName = scene?._meta?.roomn || '';
	$: run_on_save = scene?._run_on_ingest || false;
	$: scene_enable_val = scene?._enabled || false;
	$: allowed_for_3rd_party = scene?._meta?.integrations || false;

	$: whens =
		scene?.if?.or ||
		(scene?.if?.and as any[])?.find((el: any) => el?.or && el.or?.every((el: any) => !el.indailyint))
			?.or ||
		[];
	$: thens = scene?.do || [];
	$: elses = scene?.else || [];
	$: active_time = (scene?.if?.and as any[])?.find((el: any) => el && el.indailyint)
		? [(scene?.if?.and as any[])?.find((el: any) => el && el.indailyint)]
		: (scene?.if?.and as any[])?.find((el: any) => el.or && el?.or?.every((e: any) => e.indailyint))
			? (scene?.if?.and as any[])?.find((el: any) => el?.or?.every((e: any) => e.indailyint))?.or
			: [
					{
						indailyint: { dows: '1 2 3 4 5 6 7', startat: '00:00', dur: 1439 },
					},
				];

	$: duplicateInfo = scene ? getDuplicateInfo(scene, $scenes) : null;

	// Modal states
	let showAddEditModal = false;
	let showAddEditCondition = false;
	let when_info = { mode: 'add', type: 'or', main_ind: -1, sub_ind: -1 };
	let element;
	let showAddEditAction = false;
	let do_info = { mode: 'add', type: 'action', ind: -1 };
	let showEditActiveTime = false;
	let editedInterval;
	let showDuplicateConfirmModal = false;
	let showPostDuplicateModal = false;
	let duplicatedSceneId = '';
	let isDuplicating = false;
	let showScriptEditorModal = false;

	// Reset edit state when modal is closed (including when closed by SceneWizard)
	$: if (!showAddEditModal && $edit) {
		edit.set(false);
	}

	const onTabClick = (e: CustomEvent) => {
		const selectedTabData = e.detail;
		tabIndex = selectedTabData.index;
		tabs = handleSidePanelTabClick(tabs, tabIndex);
	};

	const resetActiveTab = () => {
		tabIndex = 0;
		tabs = scenesTabsConfig;
	};

	const handleSceneEdit = () => {
		if (scene?.infinno) {
			showScriptEditorModal = true;
		} else {
			scene = _deepClone($scenes[id]) as ISceneObj;
			showAddEditModal = true;
		}
	};

	// --- Inline edit handlers ---

	const openEditConditionWizard = (e) => {
		showAddEditCondition = true;
		when_info = {
			main_ind: e.detail.main_ind,
			mode: 'edit',
			sub_ind: e.detail.sub_ind,
			type: 'and',
		};
		element =
			when_info.main_ind >= 0 && when_info.sub_ind >= 0
				? whens[when_info.main_ind]['and'][when_info.sub_ind]
				: undefined;

		scene = addRemoveDeviceIdsFromMetaInSceneObject('condition', element, scene);
	};

	const openEditActionWizard = (e) => {
		showAddEditAction = true;
		do_info = { mode: 'edit', type: 'action', ind: e.detail };
		element = do_info.ind >= 0 ? thens[do_info.ind] : undefined;

		scene = addRemoveDeviceIdsFromMetaInSceneObject('action', element, scene);
	};

	const openEditActiveTime = (e) => {
		showEditActiveTime = true;
		editedInterval = {
			ind: e.detail.ind,
			active_time: e.detail,
		};
	};

	// --- Save logic ---

	const save_scene = async (sceneToSave) => {
		sceneToSave._meta.room = sceneToSave._meta.room == '' ? -1 : sceneToSave._meta.room;
		sceneToSave._meta.roomn =
			typeof $rooms[sceneToSave._meta.room] !== 'undefined'
				? $rooms[sceneToSave._meta.room].name
				: sceneToSave._meta.room == -1
					? 'General'
					: 'Missing';

		sceneToSave._meta.name = sceneToSave._meta.name.trim();
		if (
			!$scenes[sceneToSave?._id] ||
			Number($scenes[sceneToSave?._id]?._meta?.room) !== Number(sceneToSave?._meta.room)
		) {
			sceneToSave._meta.position =
				Object.values($scenes).filter(
					(sc: any) =>
						!sc.tschem &&
						sc._gui_function !== 'alarm' &&
						Number(sc._meta?.room) === Number(sceneToSave._meta?.room),
				).length || 0;
		}

		if (
			typeof sceneToSave.name !== 'undefined' ||
			typeof sceneToSave.position !== 'undefined' ||
			typeof sceneToSave.room !== 'undefined' ||
			typeof sceneToSave.id !== 'undefined' ||
			typeof sceneToSave.image !== 'undefined' ||
			typeof sceneToSave.backgroundColor !== 'undefined'
		) {
			delete sceneToSave.position;
			delete sceneToSave.id;
			delete sceneToSave.room;
			delete sceneToSave.name;
			delete sceneToSave.image;
			delete sceneToSave.backgroundColor;
		}

		let res: any = {};

		if (sceneToSave._id === undefined && !$edit) {
			res = await postReq('/scene/add', {
				scene_script: JSON.stringify(sceneToSave),
			});
			if (res.isok && res?.data?.scene_id) {
				sceneToSave._id = res.data.scene_id;
				return sceneToSave;
			}
		}

		if (sceneToSave._id) {
			res = await postReq('/scene/edit', {
				id: sceneToSave._id,
				scene_script: JSON.stringify(sceneToSave),
			});
			if (res.isok && res?.data?.scene_id) {
				return sceneToSave;
			}
		}
	};

	const check_broken = () => {
		if (scene._meta.broken) {
			const isBroken = check_for_broken_conditions_or_actions(scene);
			if (isBroken) {
				return true;
			} else {
				scene._meta.broken = false;
				return false;
			}
		}
	};

	const handleSave = () => {
		if (validate_scene(scene) && !check_broken()) {
			save_scene(scene)
				.then((savedScene) => {
					if (!$edit) {
						scenes.addScene(savedScene);
					} else if ($edit) {
						scenes.editScene(savedScene);
					}
				})
				.catch((err) => {
					console.log(err);
				});
		}
	};

	// --- Duplicate logic ---

	const handleDuplicateScene = () => {
		showDuplicateConfirmModal = true;
	};

	const confirmDuplicateScene = async () => {
		if (isDuplicating) return;

		isDuplicating = true;

		try {
			const originalScene = $scenes[id];
			if (!originalScene) {
				throw new Error('Original scene not found');
			}

			const duplicatedScene = createSceneDuplicate(originalScene);

			if (!validate_scene(duplicatedScene)) {
				throw new Error('Duplicated scene validation failed');
			}

			const savedScene = await save_scene(duplicatedScene);
			if (!savedScene || !savedScene._id) {
				throw new Error('Failed to save duplicated scene');
			}

			scenes.addScene(savedScene);
			duplicatedSceneId = savedScene._id;

			showDuplicateConfirmModal = false;

			toast.open({
				type: 'success',
				title: $format('success_duplicate_scene', {
					default: 'Scene duplicated successfully',
				}),
			});
			showPostDuplicateModal = true;
		} catch (error) {
			console.error('Failed to duplicate scene:', error);
			showDuplicateConfirmModal = false;

			toast.open({
				type: 'danger',
				title: $format('error_duplicate_scene', {
					default: 'Failed to duplicate scene',
				}),
			});
		} finally {
			isDuplicating = false;
		}
	};

	const createSceneDuplicate = (originalScene: ISceneObj): ISceneObj => {
		const duplicate = _deepClone(originalScene) as ISceneObj;

		delete duplicate._id;
		delete duplicate._name;
		delete duplicate.id;
		delete duplicate._file_modtag;

		duplicate._meta = {
			...duplicate._meta,
			name: generateCopyName(originalScene._meta.name, $scenes),
			room: originalScene._meta.room,
			roomn: '',
			position: -1,
			cdi: originalScene._meta.cdi ? [...originalScene._meta.cdi] : undefined,
			adi: originalScene._meta.adi ? [...originalScene._meta.adi] : undefined,
			extra: undefined,
			broken: undefined,
		};

		if (originalScene._enabled) {
			duplicate._enabled = false;
		}

		duplicate._run_on_ingest = false;

		return duplicate;
	};

	const handleEditDuplicatedScene = () => {
		showPostDuplicateModal = false;
		scene = _deepClone($scenes[duplicatedSceneId]) as ISceneObj;
		id = duplicatedSceneId;
		edit.set(true);
		showAddEditModal = true;
	};

	const onCloseDuplicateConfirmModal = () => {
		showDuplicateConfirmModal = false;
	};

	const onClosePostDuplicateModal = () => {
		showPostDuplicateModal = false;
		setSidePanelState({ type: 'scene', id: duplicatedSceneId, open: true });
		duplicatedSceneId = '';
	};

	// --- Script scene handlers ---

	function onSceneConvertedToScript(e: CustomEvent<ScriptScene>) {
		scene.infinno = e.detail;
		tabIndex = 0;
	}

	function onScriptEditorClose() {
		showScriptEditorModal = false;
	}

	$: !$sidePanelState.open ? resetActiveTab() : null;
</script>

{#if typeof scene === 'object'}
	<SidepanelLayout
		title={name}
		subtitle={roomName}
		settingsIcon={canManageHomeEntities($homes.list) ? 'fat fa-pen' : undefined}
		onSettings={canManageHomeEntities($homes.list) ? handleSceneEdit : undefined}
		hideSettings={!canManageHomeEntities($homes.list)}
	>
		<svelte:component
			this={SCENE_SIDE_PANEL_CONTENT_CONFIG?.[tabIndex]}
			{id}
			{scene}
			{whens}
			{thens}
			{elses}
			{active_time}
			{run_on_save}
			{scene_enable_val}
			{allowed_for_3rd_party}
			{duplicateInfo}
			on:edit-when={openEditConditionWizard}
			on:edit-do={openEditActionWizard}
			on:edit-active-time={openEditActiveTime}
			on:duplicate={handleDuplicateScene}
			on:scene-converted-to-script={onSceneConvertedToScript}
		/>

		<SidepanelTabs slot="tabs" {tabs} on:click={onTabClick} />
	</SidepanelLayout>
{:else if id !== '-1'}
	<SidepanelLayout title={$format('title_deleted_scene', { default: 'Deleted scene' })}>
		<SidepanelContent>
			<ParameterCard
				icon="fas fa-film"
				title={$format('label_not_found_el', {
					default: 'Not found element',
				})}
				description={'ID:' + id}
				size="m"
			/>
		</SidepanelContent>
	</SidepanelLayout>
{/if}

{#if showAddEditModal}
	<SceneWizard {id} bind:showAddEditModal scene_object={scene} />
{/if}

{#if showAddEditCondition}
	<WhenStep
		backdropCloseModal
		{element}
		{when_info}
		hideWhens
		saveEditSteps
		bind:scene_object={scene}
		bind:showAddEditCondition
		on:save={handleSave}
	/>
{/if}

{#if showAddEditAction}
	<DoStep
		backdropCloseModal
		on:save={handleSave}
		{element}
		{do_info}
		bind:showAddEditAction
		hideActions
		bind:scene_object={scene}
	/>
{/if}

{#if showEditActiveTime}
	<ActiveTimeStep
		backdropCloseModal
		on:save={handleSave}
		hideInterval
		bind:showEditActiveTime
		{editedInterval}
		bind:scene_object={scene}
	/>
{/if}

<!-- Duplicate Scene Confirmation Modal -->
<Modal on:close={onCloseDuplicateConfirmModal} showModal={showDuplicateConfirmModal} hideSlotToolbar>
	<svelte:fragment slot="content">
		<Container hideSlotBottom hideSlotToolbar="true" padding="0">
			<div class="mb-s d-flex justify-content-center">
				<Icon icon="fa-light fa-triangle-exclamation text-warning" size="xxl" />
			</div>
			<div class="p-s">
				<Text styleType="body" class="mb-m text-center">
					{$format('modal_duplicate_scene_confirm', {
						default: 'Are you sure you want to duplicate this scene?',
					})}
				</Text>
				<Text styleType="body-small" class="text-color-muted text-center">
					{$format('modal_duplicate_scene_description', {
						default: 'A copy of the scene will be created and will be inactive by default.',
						values: { name: scene?._meta?.name || 'Scene' },
					})}
				</Text>
			</div>
		</Container>
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-center">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			variant="secondary"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={onCloseDuplicateConfirmModal}
		>
			{capFirstChar($format('button_cancel', { default: 'Cancel' }))}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			variant="primary"
			responsiveSize={{
				sm: 'm',
			}}
			disabled={isDuplicating}
			aria-label={$format('button_duplicate_scene', { default: 'Duplicate scene' })}
			on:click={confirmDuplicateScene}
		>
			{#if isDuplicating}
				{capFirstChar($format('duplicate_label', { default: 'Duplicating...' }))}
			{:else}
				{capFirstChar($format('duplicate_label', { default: 'Duplicate' }))}
			{/if}
		</Button>
	</div>
</Modal>

<!-- Post Duplicate Modal -->
<Modal
	on:close={onClosePostDuplicateModal}
	showModal={showPostDuplicateModal}
	title={$format('success_duplicate_scene', {
		default: 'Scene Duplicated',
	})}
>
	<svelte:fragment slot="content">
		<Container hideSlotBottom hideSlotTop padding="0">
			<div class="p-m">
				<Text styleType="body" class="mb-m">
					{$format('modal_scene_duplicated_edit_question', {
						default: 'Would you like to edit the newly created scene now?',
					})}
				</Text>
			</div>
		</Container>
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			variant="secondary"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={onClosePostDuplicateModal}
		>
			{capFirstChar($format('button_no', { default: 'No' }))}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			variant="primary"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={handleEditDuplicatedScene}
		>
			{capFirstChar($format('button_yes', { default: 'Yes' }))}
		</Button>
	</div>
</Modal>

{#if scene?.infinno}
	<Modal
		type="fullScreen"
		backdropClose={false}
		showModal={showScriptEditorModal}
		on:close={() => onScriptEditorClose()}
		title={scene?._meta?.name || 'Script Editor'}
	>
		<ScriptSceneEditor
			slot="content"
			user_data={$user_data}
			{scene}
			activeTimeCalculationHelpers={{
				_calculate_duration,
				calculateWhenEnd,
			}}
			activeTimeComponent={AddEditInterval}
			ScriptEditorClass={ShellyScriptEditor}
		/>
	</Modal>
{/if}
